/**
 * Has the app been deployed since this tab loaded?
 *
 * The backend stamps every response with `X-App-Build` (the deployed git SHA).
 * The first value this tab sees is the build it runs against; a different one
 * later means a deploy happened. No request of its own - the header rides on
 * calls the app already makes, `/api/me` and `/api/info` among them every
 * minute, so an open tab learns of a deploy within a minute of it.
 *
 * Once set, the flag stays set: during a rolling deploy replies alternate
 * between old and new tasks, and that must not make the strip blink.
 */

import { Atom } from './atom.ts';

export const newBuildAvailable = new Atom(false);

let loadedBuild: string | null = null;

export function noteServerBuild(build: string | null): void {
  if (!build) return;
  if (loadedBuild === null) {
    loadedBuild = build;
    return;
  }
  if (build !== loadedBuild) newBuildAvailable.set(true);
}

/**
 * After a deploy the old bundle's lazily loaded chunks are gone from the
 * server, so a stale tab fails the moment it needs one. Reload into the new
 * build instead - once: a second failure within a minute means reloading does
 * not help, and the strip is shown rather than looping.
 */
export function handleStaleChunk(event: Event): void {
  const KEY = 'nde:stale-chunk-reload';
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY)) || 0;
  } catch {
    /* storage blocked: fall through and show the strip */
    last = Date.now();
  }
  if (Date.now() - last < 60_000) {
    newBuildAvailable.set(true);
    return;
  }
  event.preventDefault();
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Test hook. */
export function resetAppBuild(): void {
  loadedBuild = null;
  newBuildAvailable.set(false);
}
